import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import useRealizationImageDraft from '../../hooks/realizations/useRealizationImageDraft';
import useRecipeRealizationActions from '../../hooks/realizations/useRecipeRealizationActions';
import useRecipeRealizationsFeed from '../../hooks/realizations/useRecipeRealizationsFeed';
import { Alert, Box, Button, Heading, Stack, Text } from '../ui';
import RecipeRealizationCard from './RecipeRealizationCard';
import RecipeRealizationForm from './RecipeRealizationForm';

interface RecipeRealizationsSectionProps {
  recipeId: string;
  recipeAuthorId: number;
  className?: string;
}

const RecipeRealizationsSection: React.FC<RecipeRealizationsSectionProps> = ({
  recipeId,
  recipeAuthorId,
  className,
}) => {
  const { author, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const [commentDraft, setCommentDraft] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const canPublishRealization = !!author?.id && author.id !== recipeAuthorId;
  const {
    realizations,
    loading,
    error,
    setError,
    reload,
  } = useRecipeRealizationsFeed({
    recipeId,
    authorId: author?.id,
    authLoading,
  });

  const currentAuthorRealization = useMemo(() => {
    if (!author?.id) {
      return null;
    }

    return realizations.find((realization) => realization.author_id === author.id) || null;
  }, [author?.id, realizations]);

  const otherRealizations = useMemo(() => {
    return realizations.filter((realization) => realization.id !== currentAuthorRealization?.id);
  }, [currentAuthorRealization?.id, realizations]);

  const {
    selectedFile,
    displayedImageUrl,
    removeCurrentImage,
    handleFileChange,
    handleRemoveImage,
    resetDraft,
  } = useRealizationImageDraft({
    currentImageUrl: currentAuthorRealization?.image || null,
    resetKey: `${currentAuthorRealization?.id || 'none'}:${currentAuthorRealization?.comment || ''}`,
  });

  useEffect(() => {
    setCommentDraft(currentAuthorRealization?.comment || '');
  }, [currentAuthorRealization?.comment, currentAuthorRealization?.id]);

  const reloadAfterMutation = async () => {
    await reload();
    setIsFormOpen(false);
  };

  const { submitting, handleSubmit, handleDelete } = useRecipeRealizationActions({
    recipeId,
    authorId: author?.id,
    canPublishRealization,
    currentAuthorRealization,
    commentDraft,
    selectedFile,
    removeCurrentImage,
    onReload: reloadAfterMutation,
    onToast: showToast,
    onError: setError,
    onCommentDraftChange: setCommentDraft,
    onResetDraft: () => resetDraft(fileInputRef.current),
  });

  if (authLoading || loading) {
    return <Text as="div" marginTop={5} className={className}>Chargement des realisations...</Text>;
  }

  const isRecipeAuthor = !!author?.id && author.id === recipeAuthorId;

  // If the user is the recipe author and there are no other realizations, hide the whole section
  if (isRecipeAuthor && otherRealizations.length === 0) {
    return null;
  }

  const closeForm = () => {
    setCommentDraft(currentAuthorRealization?.comment || '');
    resetDraft(fileInputRef.current);
    setIsFormOpen(false);
  };

  return (
    <Box as="section" marginTop={5} className={className} data-testid="recipe-realizations-section">
      <Heading as="h4" marginBottom={3}>Realisations</Heading>

      {!canPublishRealization && !isRecipeAuthor && (
        <Alert variant="light" bordered>
          Connecte-toi pour voir et publier des realisations.
        </Alert>
      )}

      {canPublishRealization && !currentAuthorRealization && !isFormOpen && (
        <Box marginBottom={4}>
          <Button
            type="button"
            variant="primary"
            outline
            onClick={() => setIsFormOpen(true)}
          >
            Ajouter ma realisation
          </Button>
        </Box>
      )}

      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      {canPublishRealization && isFormOpen && (
        <RecipeRealizationForm
          recipeId={recipeId}
          hasCurrentRealization={!!currentAuthorRealization}
          commentDraft={commentDraft}
          displayedImageUrl={displayedImageUrl}
          submitting={submitting}
          fileInputRef={fileInputRef}
          onCommentDraftChange={setCommentDraft}
          onFileChange={handleFileChange}
          onRemoveImage={handleRemoveImage}
          onSubmit={handleSubmit}
          onDelete={() => { void handleDelete(); }}
          onCancel={closeForm}
        />
      )}

      {currentAuthorRealization && (
        <Box marginBottom={3}>
          <RecipeRealizationCard
            realization={currentAuthorRealization}
            currentAuthor={author}
            isCurrentAuthor
            onEdit={canPublishRealization ? () => setIsFormOpen(true) : undefined}
          />
        </Box>
      )}

      {otherRealizations.length === 0 && !currentAuthorRealization ? (
        <Text tone="muted">Aucune realisation pour le moment.</Text>
      ) : otherRealizations.length === 0 ? (
        <Text tone="muted">Aucune autre realisation pour le moment.</Text>
      ) : (
        <Stack gap={3}>
          {otherRealizations.map((realization) => (
            <RecipeRealizationCard key={realization.id} realization={realization} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default RecipeRealizationsSection;
